import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Spin } from "antd";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { LocalStorageService } from "../../base/services/local-storage.service";
import { describeAmountDue, formatAmountDue, owesPayment } from "./amountDue";
import BillingProfileForm from "./BillingProfileForm";
import { billingApi } from "./billingApi";
import { BillingCode, describeBillingError } from "./billingCodes";
import { formatProfileAddress } from "./billingProfile";
import { buildConsent, consentInterval, renderConsentText } from "./consent";
import { getCountryName } from "./countries";
import { enterpriseTermsBilled } from "./enterprise";
import { readDecline, settlePayment } from "./paymentAction";
import { Retry, resolveConfirmError, resolveSetupIntentStatus } from "./setupIntents";
import { getStripe, PAYMENT_ELEMENTS_OPTIONS } from "./stripeClient";
import { usePlanLabel } from "./usePlanLabel";
import "./index.scss";

// Saving a card, the one way it is done: through a SetupIntent and Stripe's
// Payment Element. It replaces addPaymentMethodToOrganization, which filed a
// card created in the browser with no SetupIntent — so no 3-D Secure when it was
// saved, and no record of what the cardholder agreed to.
//
//   1. Billing details. Stripe needs the address to raise a valid invoice, so an
//      incomplete profile is filled in first (BillingProfileForm).
//   2. The agreement. The card is charged later with nobody present, so the
//      cardholder agrees to that, and the SetupIntent is created with it.
//   3. The card, in the Payment Element mounted from that SetupIntent's client
//      secret — which is what keeps the payment method types in step with the
//      SetupIntent (cards only). Nothing here names types or a mode of its own.
//   4. Complete. The server makes the card the default, applies any pending plan
//      change, resumes a paused subscription and pays what is owed. If the bank
//      wants 3-D Secure on that payment, it is run here, and the rest of what is
//      owed after it (paymentAction.js, settlePayment).
//
// When something is owed (amountDue.js), the agreement says how much it comes to,
// and that amount goes with it as `amountShown`: the server refuses one that is
// no longer right, and the page shows the new amount to confirm instead.
//
// `onDone` gets the BillingStatus that comes out of it, and `{ error }`: the
// bank's refusal of the payment, when it turned it down — answered 409
// PAYMENT_FAILED, with the status it left. What that means — the organization is
// back in, still waiting on Stripe, still owes something — is the caller's to
// show. A refusal is never a wait (billingActions.js).

const Step = Object.freeze({
   LOADING: "loading",
   PROFILE: "profile",
   CONSENT: "consent",
   CARD: "card"
});

/** Where Stripe sends the browser back to, when a bank's check has to redirect. */
export const CARD_SAVED_PATH = "/billing/card-saved";

// The failures the billing details are the answer to.
const PROFILE_CODES = new Set([
   BillingCode.BILLING_PROFILE_INCOMPLETE,
   BillingCode.BILLING_PROFILE_INVALID,
   BillingCode.NO_STRIPE_CUSTOMER,
   BillingCode.TAX_ID_INVALID
]);

// Once Stripe has confirmed the card the SetupIntent is spent, so a failure to
// complete it is answered one of two ways. These are the ones only another card
// gets past: the card was set up but the payment it was asked to make was
// declined, or the intent turned out not to be one the server will take.
// Anything else — a dropped connection, a busy lock, Stripe having a moment — is
// worth completing the same intent again. (The setup_intent.succeeded webhook
// does the same work on its own, so nothing is lost by a browser that gives up.)
const NEEDS_NEW_CARD_CODES = new Set([
   BillingCode.PAYMENT_FAILED,
   BillingCode.SETUP_INTENT_FOREIGN,
   BillingCode.SETUP_INTENT_NOT_SUCCEEDED
]);

// The billing details as one block, with the way back to them.
function ProfileSummary({ profile, onEdit }) {
   return (
      <div className="billing-summary">
         <div>
            <span className="billing-summary__label">Billed to</span>
            <span className="billing-summary__value">{profile?.name}</span>
            <span className="billing-summary__detail">
               {formatProfileAddress(profile, getCountryName)}
            </span>
         </div>
         <Button type="link" onClick={onEdit}>
            Edit
         </Button>
      </div>
   );
}

// The card, inside the <Elements> the SetupIntent's client secret was given to.
function CardConfirmForm({ organizationId, intent, profile, submitLabel, onDone, onRestart }) {
   const stripe = useStripe();
   const elements = useElements();
   const [isReady, setIsReady] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const [error, setError] = useState(null);
   // Set once the card is confirmed and completing it failed: which way on.
   const [retry, setRetry] = useState(null);
   const [hostedInvoiceUrl, setHostedInvoiceUrl] = useState(null);

   // A SetupIntent the server handed out already spent — a card confirmed in an
   // earlier attempt whose saving never finished — can't take the card typed
   // here, so the form says so before anything is typed, and offers what can
   // still be done (setupIntents.js). Confirming is where a failure to look
   // this up is caught.
   useEffect(() => {
      if (!stripe) return undefined;
      let isCurrent = true;

      stripe
         .retrieveSetupIntent(intent.clientSecret)
         .then(({ setupIntent }) => {
            const unusable = isCurrent ? resolveSetupIntentStatus(setupIntent?.status) : null;
            if (!unusable) return;
            setError(unusable.message);
            setRetry(unusable.retry);
         })
         .catch(() => {});

      return () => {
         isCurrent = false;
      };
   }, [stripe, intent.clientSecret]);

   // The billing details the card form would otherwise ask for again. The tax
   // number is deliberately not among them — it has no place on a card. Built
   // once per profile: the Payment Element takes its default values when it
   // mounts, and treats a change to them afterwards as a mistake.
   const paymentElementOptions = useMemo(
      () => ({
         defaultValues: {
            billingDetails: {
               name: profile?.name || undefined,
               email: profile?.email || undefined,
               address: {
                  line1: profile?.address?.line1 || undefined,
                  line2: profile?.address?.line2 || undefined,
                  city: profile?.address?.city || undefined,
                  state: profile?.address?.state || undefined,
                  postal_code: profile?.address?.postalCode || undefined,
                  country: profile?.address?.country || undefined
               }
            }
         }
      }),
      [profile]
   );

   // Hand the confirmed card to the server, and see through whatever the bank
   // then asks of the payment it makes.
   const complete = async () => {
      setRetry(null);

      try {
         let status;
         let settled;

         try {
            status = await billingApi.completeSetupIntent({
               organizationId,
               setupIntentId: intent.setupIntentId
            });
         } catch (failure) {
            // The card is saved and the bank declined the payment: 409, with the
            // status it left. Not a failure to complete, but a refusal, which
            // goes the way a refused bank's check does below.
            settled = readDecline(failure);

            // Not a failure to complete either: the bank wants 3-D Secure on
            // the payment the card was asked to make, settled like any status
            // that asks for action.
            if (!settled && failure.code !== BillingCode.PAYMENT_ACTION_REQUIRED) throw failure;
            status = { requiresAction: true };
         }

         settled = settled ?? (await settlePayment(status, organizationId));

         // The card is on file, but the bank turned down the payment it was
         // asked to make. This SetupIntent is spent, so the way on is the card
         // form again, for the same card or another one.
         if (settled.error) {
            setError(
               settled.error.message || describeBillingError({ code: BillingCode.PAYMENT_FAILED })
            );
            setRetry(Retry.NEW_CARD);
         }
         if (settled.needsHostedPage) setHostedInvoiceUrl(settled.status?.hostedInvoiceUrl ?? null);

         // The caller is told about a refusal too, so that it never waits for
         // access, or announces it, after a payment that failed.
         onDone(settled.status, { error: settled.error });
      } catch (failure) {
         setError(describeBillingError(failure));
         setRetry(NEEDS_NEW_CARD_CODES.has(failure.code) ? Retry.NEW_CARD : Retry.COMPLETE);
      }
   };

   const retryComplete = async () => {
      if (isSaving) return;

      setIsSaving(true);
      setError(null);
      await complete();
      setIsSaving(false);
   };

   const handleSubmit = async () => {
      if (!stripe || !elements || isSaving) return;

      setIsSaving(true);
      setError(null);

      try {
         const { error: confirmError, setupIntent } = await stripe.confirmSetup({
            elements,
            confirmParams: { return_url: `${window.location.origin}${CARD_SAVED_PATH}` },
            redirect: "if_required"
         });

         // A card the bank refused, a challenge that failed: the SetupIntent can
         // take another card, so the form stays as it is. Unless Stripe refused
         // because it can't take one any more, which it words only as "A
         // processing error occurred.".
         if (confirmError) {
            const unusable = resolveConfirmError(confirmError);
            setError(unusable?.message ?? confirmError.message ?? describeBillingError({}));
            if (unusable) setRetry(unusable.retry);
            return;
         }

         let confirmed = setupIntent;

         if (confirmed?.status === "requires_action") {
            const next = await stripe.handleNextAction({ clientSecret: intent.clientSecret });
            if (next.error) {
               setError(next.error.message ?? describeBillingError({}));
               return;
            }
            confirmed = next.setupIntent;
         }

         if (confirmed?.status !== "succeeded" && confirmed?.status !== "processing") {
            setError(describeBillingError({ code: BillingCode.SETUP_INTENT_NOT_SUCCEEDED }));
            return;
         }

         await complete();
      } catch (failure) {
         // Stripe.js itself threw — not loaded, torn down. The card was never
         // confirmed, so the same form can simply be sent again.
         setError(describeBillingError(failure));
      } finally {
         setIsSaving(false);
      }
   };

   return (
      <>
         <div className="billing-card-field">
            <PaymentElement options={paymentElementOptions} onReady={() => setIsReady(true)} />
         </div>

         {error && (
            <div className="billing-form__message" role="alert">
               {error}
            </div>
         )}

         {hostedInvoiceUrl && (
            <p className="billing-note">
               You can also finish the payment on{" "}
               <a href={hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                  Stripe's secure payment page
               </a>
               .
            </p>
         )}

         <div className="billing-actions">
            {retry === Retry.NEW_CARD && (
               <Button className="kapSurveyPrimaryButton" onClick={onRestart}>
                  Try a different card
               </Button>
            )}
            {retry === Retry.COMPLETE && (
               <Button className="kapSurveyPrimaryButton" onClick={retryComplete} loading={isSaving}>
                  Try again
               </Button>
            )}
            {!retry && (
               <Button
                  className="kapSurveyPrimaryButton"
                  onClick={handleSubmit}
                  loading={isSaving}
                  disabled={!stripe || !elements || !isReady}
               >
                  {submitLabel}
               </Button>
            )}
         </div>
      </>
   );
}

function CardSetup({ organizationId, status, onDone, onCancel, submitLabel = "Save card" }) {
   const [step, setStep] = useState(Step.LOADING);
   const [profile, setProfile] = useState(null);
   const [consentAccepted, setConsentAccepted] = useState(false);
   const [intent, setIntent] = useState(null);
   const [isStarting, setIsStarting] = useState(false);
   const [error, setError] = useState(null);
   // What saving the card charges at once: the status's own when it carries it,
   // or read fresh when it owes something and doesn't — the status a sign in or
   // a refresh hands over reads no invoices, so it never has the amount.
   const [paymentStatus, setPaymentStatus] = useState(status);
   const [isReadingAmount, setIsReadingAmount] = useState(
      () => owesPayment(status) && !status?.amountDue
   );

   const interval = consentInterval(status?.isMonthly);
   const planLabel = usePlanLabel(status?.packetId, status?.isMonthly, enterpriseTermsBilled(status));
   const organizationName = LocalStorageService.getItem("organizationName");
   const amountDue = paymentStatus?.amountDue ?? null;
   const amountSentence = describeAmountDue(paymentStatus);
   const amountText = formatAmountDue(paymentStatus);

   // A status read since — the billing page's, kept in the store — carries
   // the amount as it stands now.
   useEffect(() => {
      if (status?.amountDue) setPaymentStatus(status);
   }, [status]);

   useEffect(() => {
      if (!isReadingAmount) return undefined;
      let isCurrent = true;

      billingApi
         .getBillingStatus(organizationId)
         .then((fresh) => {
            if (isCurrent && fresh) setPaymentStatus(fresh);
         })
         // Not read: the agreement goes without an amount, as before there was
         // one, and the server's own check still stands behind it.
         .catch(() => {})
         .finally(() => {
            if (isCurrent) setIsReadingAmount(false);
         });

      return () => {
         isCurrent = false;
      };
   }, [organizationId, isReadingAmount]);

   useEffect(() => {
      let isCurrent = true;

      billingApi
         .getBillingProfile(organizationId)
         .then((saved) => {
            if (!isCurrent) return;
            setProfile(saved);
            setStep(saved?.isComplete ? Step.CONSENT : Step.PROFILE);
         })
         .catch(() => {
            // No profile yet, or none that could be read: the form is where
            // either is sorted out, and it says which.
            if (isCurrent) setStep(Step.PROFILE);
         });

      return () => {
         isCurrent = false;
      };
   }, [organizationId]);

   const handleProfileSaved = (saved) => {
      setProfile(saved);
      setError(null);
      setStep(saved?.isComplete ? Step.CONSENT : Step.PROFILE);
   };

   const startCardEntry = async () => {
      if (!consentAccepted || isStarting) return;

      setIsStarting(true);
      setError(null);

      try {
         const created = await billingApi.createSetupIntent({
            organizationId,
            consent: buildConsent({ interval, amountShown: amountDue?.amount })
         });

         if (!created?.clientSecret || !created?.setupIntentId) {
            setError(describeBillingError({}));
            return;
         }

         setIntent(created);
         setStep(Step.CARD);
      } catch (failure) {
         setError(describeBillingError(failure));
         if (PROFILE_CODES.has(failure.code)) setStep(Step.PROFILE);

         // What is owed changed since it was shown (a day passed, an invoice
         // was paid). The new amount is read and shown, and agreed to again.
         if (failure.code === BillingCode.CONSENT_AMOUNT_MISMATCH) {
            setConsentAccepted(false);
            const fresh = await billingApi.getBillingStatus(organizationId).catch(() => null);
            if (fresh) setPaymentStatus(fresh);
         }
      } finally {
         setIsStarting(false);
      }
   };

   // A spent SetupIntent is let go of, and the agreement is shown again before
   // a new one is made with it.
   const restart = () => {
      setIntent(null);
      setError(null);
      setStep(Step.CONSENT);
   };

   return (
      <div className="billing-card-setup">
         {step === Step.LOADING && (
            <div className="billing-loading">
               <Spin />
            </div>
         )}

         {step === Step.PROFILE && (
            <>
               <h3 className="billing-section-title">Billing details</h3>
               {error && (
                  <div className="billing-form__message" role="alert">
                     {error}
                  </div>
               )}
               <BillingProfileForm
                  organizationId={organizationId}
                  onSaved={handleProfileSaved}
                  onCancel={onCancel}
                  submitLabel="Save and continue"
               />
            </>
         )}

         {step === Step.CONSENT && (
            <>
               <h3 className="billing-section-title">Billing details</h3>
               <ProfileSummary profile={profile} onEdit={() => setStep(Step.PROFILE)} />

               <h3 className="billing-section-title">Card payments</h3>
               {isReadingAmount ? (
                  <div className="billing-loading">
                     <Spin />
                  </div>
               ) : (
                  amountSentence && (
                     <p className="billing-amount-due" role="status">
                        {amountSentence}
                     </p>
                  )
               )}
               <Checkbox
                  className="billing-consent"
                  checked={consentAccepted}
                  onChange={(event) => setConsentAccepted(event.target.checked)}
               >
                  {renderConsentText({ organizationName, planLabel, interval })}
               </Checkbox>

               {error && (
                  <div className="billing-form__message" role="alert">
                     {error}
                  </div>
               )}

               <div className="billing-actions">
                  {onCancel && (
                     <Button className="defaultButton" onClick={onCancel} disabled={isStarting}>
                        Cancel
                     </Button>
                  )}
                  <Button
                     className="kapSurveyPrimaryButton"
                     onClick={startCardEntry}
                     loading={isStarting}
                     disabled={!consentAccepted || isReadingAmount}
                  >
                     Continue to card details
                  </Button>
               </div>
            </>
         )}

         {step === Step.CARD && intent && (
            <>
               <h3 className="billing-section-title">Card details</h3>
               {/* One Elements group per SetupIntent: the client secret is fixed
                   for the life of the group, so a new intent gets a new one. */}
               <Elements
                  key={intent.clientSecret}
                  stripe={getStripe()}
                  options={{ ...PAYMENT_ELEMENTS_OPTIONS, clientSecret: intent.clientSecret }}
               >
                  <CardConfirmForm
                     organizationId={organizationId}
                     intent={intent}
                     profile={profile}
                     submitLabel={amountText ? `Save card and pay ${amountText}` : submitLabel}
                     onDone={onDone}
                     onRestart={restart}
                  />
               </Elements>
               <p className="billing-note">
                  Your card details go straight to Stripe and never reach KAPsurvey.
               </p>
            </>
         )}
      </div>
   );
}

export default CardSetup;
